import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Car, Users, RefreshCw, UserRound, LogOut, Plus, Pencil, Trash2, X, Check, Loader2 } from "lucide-react";
import api from "../lib/api";
import { formatCHF } from "../lib/format";

const TABS = [
  { key: "modelle", label: "Modelle", icon: Car },
  { key: "leads", label: "Leads", icon: Users },
  { key: "preise", label: "Preisabgleich", icon: RefreshCw },
  { key: "berater", label: "Berater", icon: UserRound },
];

const inputCls =
  "w-full bg-neutral-900 border border-neutral-700 px-3 py-2.5 text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-white transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-1.5";

const EMPTY_MODEL = {
  name: "", variant: "", category: "SUV", drivetrains: "", price_from: "", power: "",
  range_km: "", consumption: "", seats: "", description: "", image_url: "", gallery: "",
  kia_url: "", active: true, finder_usages: "", finder_sizes: "", fleet_benefits: "",
};

function toForm(m) {
  if (!m) return EMPTY_MODEL;
  return {
    ...m,
    variant: m.variant || "",
    drivetrains: (m.drivetrains || []).join(", "),
    price_from: m.price_from ?? "",
    range_km: m.range_km ?? "",
    seats: m.seats ?? "",
    power: m.power || "",
    consumption: m.consumption || "",
    gallery: (m.gallery || []).join(", "),
    finder_usages: (m.finder_usages || []).join(", "),
    finder_sizes: (m.finder_sizes || []).join(", "),
    fleet_benefits: (m.fleet_benefits || []).join("; "),
  };
}

function toPayload(f) {
  const split = (s, sep = ",") => s ? s.split(sep).map((x) => x.trim()).filter(Boolean) : [];
  return {
    name: f.name,
    variant: f.variant || null,
    category: f.category,
    drivetrains: split(f.drivetrains),
    price_from: f.price_from === "" ? null : Number(f.price_from),
    power: f.power || null,
    range_km: f.range_km === "" ? null : Number(f.range_km),
    consumption: f.consumption || null,
    seats: f.seats === "" ? null : Number(f.seats),
    description: f.description,
    image_url: f.image_url,
    gallery: split(f.gallery),
    kia_url: f.kia_url,
    active: !!f.active,
    finder_usages: split(f.finder_usages),
    finder_sizes: split(f.finder_sizes),
    fleet_benefits: split(f.fleet_benefits, ";"),
  };
}

function ModelEditor({ model, onClose, onSaved }) {
  const [form, setForm] = useState(() => toForm(model));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (model?.id) await api.put(`/admin/models/${model.id}`, payload);
      else await api.post("/admin/models", payload);
      toast.success("Modell gespeichert");
      onSaved();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="model-editor">
      <form onSubmit={save} className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white font-medium text-lg">{model ? "Modell bearbeiten" : "Neues Modell"}</h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="model-editor-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Modellname *</label><input data-testid="editor-name" required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className={labelCls}>Variante</label><input className={inputCls} value={form.variant} onChange={(e) => set("variant", e.target.value)} placeholder="z. B. GT-Line" /></div>
          <div>
            <label className={labelCls}>Kategorie *</label>
            <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category">
              {["Kleinwagen", "Kompakt-SUV", "Limousine", "Kombi", "SUV", "Nutzfahrzeug"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Antriebe (kommagetrennt)</label><input className={inputCls} value={form.drivetrains} onChange={(e) => set("drivetrains", e.target.value)} placeholder="Elektro, Hybrid" /></div>
          <div><label className={labelCls}>Preis ab (CHF)</label><input data-testid="editor-price" type="number" className={inputCls} value={form.price_from} onChange={(e) => set("price_from", e.target.value)} placeholder="34950" /></div>
          <div><label className={labelCls}>Leistung</label><input className={inputCls} value={form.power} onChange={(e) => set("power", e.target.value)} placeholder="204 PS" /></div>
          <div><label className={labelCls}>Reichweite (km, WLTP)</label><input type="number" className={inputCls} value={form.range_km} onChange={(e) => set("range_km", e.target.value)} /></div>
          <div><label className={labelCls}>Verbrauch</label><input className={inputCls} value={form.consumption} onChange={(e) => set("consumption", e.target.value)} placeholder="14.9 kWh/100 km" /></div>
          <div><label className={labelCls}>Sitzplätze</label><input type="number" className={inputCls} value={form.seats} onChange={(e) => set("seats", e.target.value)} /></div>
          <div><label className={labelCls}>KIA.ch Link</label><input className={inputCls} value={form.kia_url} onChange={(e) => set("kia_url", e.target.value)} /></div>
        </div>
        <div><label className={labelCls}>Bild-URL</label><input className={inputCls} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} /></div>
        <div><label className={labelCls}>Galerie (URLs, kommagetrennt)</label><input className={inputCls} value={form.gallery} onChange={(e) => set("gallery", e.target.value)} /></div>
        <div><label className={labelCls}>Beschreibung</label><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Finder: Einsatzbereiche</label><input className={inputCls} value={form.finder_usages} onChange={(e) => set("finder_usages", e.target.value)} placeholder="aussendienst, langstrecke" /></div>
          <div><label className={labelCls}>Finder: Grössen</label><input className={inputCls} value={form.finder_sizes} onChange={(e) => set("finder_sizes", e.target.value)} placeholder="suv, kompakt" /></div>
        </div>
        <div><label className={labelCls}>Flotten-Vorteile (mit ; trennen)</label><input className={inputCls} value={form.fleet_benefits} onChange={(e) => set("fleet_benefits", e.target.value)} /></div>
        <label className="flex items-center gap-3 text-sm text-neutral-300">
          <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-white" data-testid="editor-active" />
          Modell aktiv (auf Landingpage sichtbar)
        </label>
        <button data-testid="editor-save" type="submit" disabled={saving} className="w-full bg-white text-neutral-950 font-semibold text-sm py-3 hover:bg-neutral-200 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {saving && <Loader2 size={15} className="animate-spin" />} Speichern
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("modelle");
  const [models, setModels] = useState([]);
  const [leads, setLeads] = useState([]);
  const [changes, setChanges] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [editor, setEditor] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, l, c, b] = await Promise.all([
        api.get("/admin/models"),
        api.get("/admin/leads"),
        api.get("/admin/price-changes"),
        api.get("/consultants"),
      ]);
      setModels(m.data);
      setLeads(l.data);
      setChanges(c.data);
      setConsultants(b.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const deleteModel = async (m) => {
    if (!window.confirm(`${m.name} wirklich entfernen?`)) return;
    await api.delete(`/admin/models/${m.id}`);
    toast.success("Modell entfernt");
    load();
  };

  const toggleActive = async (m) => {
    const payload = { ...m };
    delete payload.id;
    delete payload.updated_at;
    payload.active = !m.active;
    await api.put(`/admin/models/${m.id}`, payload);
    load();
  };

  const runPriceCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/admin/price-check");
      toast.success(`Abgleich fertig: ${data.checked} geprüft, ${data.changes_detected} Änderungen erkannt`);
      if (data.not_readable?.length) toast.info(`Nicht lesbar: ${data.not_readable.join(", ")}`);
      load();
    } catch {
      toast.error("Preisabgleich fehlgeschlagen");
    } finally {
      setChecking(false);
    }
  };

  const resolveChange = async (c, action) => {
    await api.post(`/admin/price-changes/${c.id}/${action}`);
    toast.success(action === "approve" ? "Preis übernommen" : "Änderung ignoriert");
    load();
  };

  const setLeadStatus = async (lead, status) => {
    await api.patch(`/admin/leads/${lead.id}`, { status });
    load();
  };

  const saveConsultant = async (c) => {
    const payload = { ...c, placeholder: !c.photo_url };
    delete payload.id;
    await api.put(`/admin/consultants/${c.id}`, payload);
    toast.success("Berater gespeichert");
    load();
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-neutral-950 text-neutral-200 flex flex-col md:flex-row">
      <aside className="md:w-60 border-b md:border-b-0 md:border-r border-neutral-800 p-5 flex md:flex-col gap-2 md:min-h-screen overflow-x-auto">
        <div className="hidden md:block mb-8">
          <div className="font-display font-bold text-white tracking-tight">Künzler &amp; Sauber</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold mt-1">Admin</div>
        </div>
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.key ? "bg-white text-neutral-950" : "text-neutral-400 hover:text-white"}`}
          >
            <t.icon size={15} /> {t.label}
            {t.key === "preise" && changes.length > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5">{changes.length}</span>
            )}
          </button>
        ))}
        <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-500 hover:text-white md:mt-auto">
          <LogOut size={15} /> Abmelden
        </button>
      </aside>

      <main className="flex-1 p-5 sm:p-10 max-w-6xl">
        {tab === "modelle" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-medium text-white tracking-tight">KIA Modelle</h2>
              <button data-testid="admin-new-model" onClick={() => setEditor({})} className="flex items-center gap-2 bg-white text-neutral-950 text-sm font-semibold px-5 py-2.5 hover:bg-neutral-200 transition-colors">
                <Plus size={15} /> Neues Modell
              </button>
            </div>
            <div className="border border-neutral-800 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-800">
                    <th className="p-4">Modell</th><th className="p-4">Preis ab</th><th className="p-4">Antrieb</th><th className="p-4">Status</th><th className="p-4">Aktualisiert</th><th className="p-4 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((m) => (
                    <tr key={m.id} data-testid={`admin-model-row-${m.id}`} className="border-b border-neutral-900 hover:bg-neutral-900/50">
                      <td className="p-4 font-medium text-white">{m.name}</td>
                      <td className="p-4">{m.price_from ? formatCHF(m.price_from) : "–"}</td>
                      <td className="p-4 text-neutral-400">{(m.drivetrains || []).join(", ")}</td>
                      <td className="p-4">
                        <button data-testid={`admin-toggle-${m.id}`} onClick={() => toggleActive(m)} className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 ${m.active ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-800 text-neutral-500"}`}>
                          {m.active ? "Aktiv" : "Inaktiv"}
                        </button>
                      </td>
                      <td className="p-4 text-neutral-500 text-xs">{m.updated_at ? new Date(m.updated_at).toLocaleDateString("de-CH") : "–"}</td>
                      <td className="p-4 text-right">
                        <button data-testid={`admin-edit-${m.id}`} onClick={() => setEditor(m)} className="p-2 text-neutral-400 hover:text-white"><Pencil size={15} /></button>
                        <button data-testid={`admin-delete-${m.id}`} onClick={() => deleteModel(m)} className="p-2 text-neutral-400 hover:text-red-400"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <h2 className="text-2xl font-medium text-white tracking-tight mb-8">Leads</h2>
            {leads.length === 0 && <p className="text-neutral-500 text-sm">Noch keine Anfragen vorhanden.</p>}
            <div className="space-y-4">
              {leads.map((l) => (
                <div key={l.id} data-testid={`lead-row-${l.id}`} className="border border-neutral-800 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="text-white font-medium">{l.firma}</span>
                      <span className="text-neutral-500 text-sm"> · {l.vorname} {l.nachname}</span>
                    </div>
                    <select
                      data-testid={`lead-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => setLeadStatus(l, e.target.value)}
                      className="bg-neutral-900 border border-neutral-700 text-xs px-3 py-1.5 text-neutral-300"
                    >
                      <option value="neu">Neu</option>
                      <option value="in_bearbeitung">In Bearbeitung</option>
                      <option value="erledigt">Erledigt</option>
                    </select>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-400">
                    <span>{l.email}</span>
                    <span>{l.telefon}</span>
                    <span>Modell: <strong className="text-neutral-200">{l.modell || "Beratung"}</strong></span>
                    <span>{l.kauf_leasing || "–"} {l.eintausch ? `· Eintausch: ${l.eintausch}` : ""}</span>
                  </div>
                  {l.bemerkungen && <p className="mt-2 text-xs text-neutral-500">{l.bemerkungen}</p>}
                  <div className="mt-2 text-[10px] text-neutral-600">{new Date(l.created_at).toLocaleString("de-CH")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "preise" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-medium text-white tracking-tight">Preisabgleich mit KIA.ch</h2>
              <button data-testid="admin-run-price-check" onClick={runPriceCheck} disabled={checking} className="flex items-center gap-2 bg-white text-neutral-950 text-sm font-semibold px-5 py-2.5 hover:bg-neutral-200 transition-colors disabled:opacity-60">
                {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Abgleich starten
              </button>
            </div>
            <p className="text-sm text-neutral-500 mb-8 max-w-2xl">
              Der Abgleich prüft die öffentlich auf KIA.ch ausgewiesenen Preise. Erkannte Änderungen werden hier zur Prüfung angezeigt – nichts wird automatisch übernommen.
            </p>
            {changes.length === 0 && <p className="text-neutral-500 text-sm">Keine offenen Preisänderungen.</p>}
            <div className="space-y-4">
              {changes.map((c) => (
                <div key={c.id} data-testid={`price-change-${c.id}`} className="border border-amber-500/30 bg-amber-500/5 p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-400 mb-1">Preisänderung erkannt</div>
                    <div className="text-white font-medium">{c.model_name}</div>
                    <div className="text-sm text-neutral-400 mt-1">
                      bisher {formatCHF(c.old_price)} → neu <strong className="text-white">{formatCHF(c.new_price)}</strong>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`price-approve-${c.id}`} onClick={() => resolveChange(c, "approve")} className="flex items-center gap-2 bg-emerald-500 text-neutral-950 text-xs font-semibold px-4 py-2.5 hover:bg-emerald-400 transition-colors">
                      <Check size={14} /> Übernehmen
                    </button>
                    <button data-testid={`price-ignore-${c.id}`} onClick={() => resolveChange(c, "ignore")} className="flex items-center gap-2 border border-neutral-700 text-neutral-300 text-xs font-semibold px-4 py-2.5 hover:border-neutral-500 transition-colors">
                      <X size={14} /> Ignorieren
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "berater" && (
          <div>
            <h2 className="text-2xl font-medium text-white tracking-tight mb-8">Verkaufsberater</h2>
            <div className="space-y-6 max-w-xl">
              {consultants.map((c, i) => (
                <form
                  key={c.id}
                  data-testid={`consultant-form-${i}`}
                  className="border border-neutral-800 p-6 space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target);
                    saveConsultant({ ...c, name: fd.get("name"), phone: fd.get("phone"), email: fd.get("email"), photo_url: fd.get("photo_url") });
                  }}
                >
                  <div><label className={labelCls}>Name</label><input name="name" defaultValue={c.name} className={inputCls} data-testid={`consultant-name-input-${i}`} /></div>
                  <div><label className={labelCls}>Telefon</label><input name="phone" defaultValue={c.phone} className={inputCls} /></div>
                  <div><label className={labelCls}>E-Mail</label><input name="email" defaultValue={c.email} className={inputCls} /></div>
                  <div><label className={labelCls}>Foto-URL (leer = Platzhalter)</label><input name="photo_url" defaultValue={c.photo_url} className={inputCls} /></div>
                  <button type="submit" data-testid={`consultant-save-${i}`} className="bg-white text-neutral-950 text-sm font-semibold px-5 py-2.5 hover:bg-neutral-200 transition-colors">
                    Speichern
                  </button>
                </form>
              ))}
            </div>
          </div>
        )}
      </main>

      {editor !== null && (
        <ModelEditor model={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </div>
  );
}
